import {rememberLanStaff} from './lan-staff';
import {validateSmmDailyReports} from './daily-report';
import {validateSmmChannelStatus} from './channel-status';
import {validateCapacityPoolReadback} from '../../../contracts/capacity-pool-v1';
import {validateMobilePrintHealthSnapshot} from '../../../contracts/mobile-print-health-v1';
import {validateMobileBusinessDayReceipts} from '../../../contracts/mobile-business-day-v1';
import {mobileDrinkResolution} from '../../../contracts/meal-drink-resolution-v1.ts';
import type {MobilePendingDrinks} from '../../../contracts/mobile-pending-drinks-v1.ts';
import {readSmmLocalWorkspace,writeSmmLocalWorkspace} from './persistence';

import {unavailableOperationalSnapshot} from './operational-view';
import {validateMobileOrderPage,normalizeMobileOrderQuery,MobileOrderPageError,type MobileOrderQuery} from '../../../contracts/mobile-order-pages-v1';
import {diagnosticFetch,recordSyncDiagnostic} from './sync-diagnostics';
import type {SmmRuntimePort,SmmReadModelSnapshot,SmmPendingIntent,SmmCommandResult,SmmOperationalFailure,SmmStoreCommandInput} from './product-types';
import type {SmmLanOrderRequest,SmmLanSubmissionReadbackResponse,SmmLanFulfillmentRequest,SmmLanFulfillmentResponse} from '../../../contracts/smm-lan-v1';
import {createSmmLanOrderAdapter,type SmmLanTransport} from './smt-lan-adapter';
import {createPwaLanTransport,readSmmLanPwaConfig} from './pwa-lan';
import {createPwaCloudTransport} from './pwa-cloud';
import {readSmmStaffSession} from './pwa-staff';
import {reconcileSmmConfig,subscribeSmmConfigChanges} from './config-sync';

function webSmtAcceptanceMode(){
  if(typeof window==='undefined')return false;
  return new URLSearchParams(window.location.search).get('target')==='web-smt';
}
export function storeCommandMessage(code:string){
  if(code==='WEB_COMMAND_PAYMENT_CHANGED')return '金額、付款方式或憑證已更新，請重新讀取後核對。';
  if(code==='PAYMENT_EVIDENCE_NOT_FOUND')return '這張單未有付款憑證，請在 Pad 核對。';
  if(code==='PAYMENT_ALREADY_VERIFIED'||code==='PAYMENT_ALREADY_CONFIRMED')return '付款已核對，不能改為未核實。';
  if(code==='MEAL_DRINK_RESOLVED')return '飲品已補選。';
  if(code==='MEAL_DRINK_PAYMENT_CONFIRMATION_REQUIRED'||code==='MEAL_DRINK_TENDER_REQUIRED'||code==='MEAL_DRINK_TENDER_INVALID')return '這份餐點已收款，差額收款或退款請在 Pad 處理。';
  if(code==='MEAL_DRINK_STALE_ORDER'||code==='MEAL_DRINK_NO_LONGER_PENDING')return '這張單已更新，請重新讀取飲品。';
  if(code==='MEAL_DRINK_POLICY_CHANGED'||code==='MEAL_DRINK_PRICE_CHANGED'||code==='MEAL_DRINK_CONFIGURATION_CHANGED'||code==='MEAL_DRINK_CHOICE_UNAVAILABLE')return '飲品或配方已更新，請重新讀取再選。';
  if(code==='MEAL_DRINK_CONFIGURATION_REQUIRED')return '請完成飲品必選項目。';
  if(code==='MEAL_DRINK_PLATFORM_ORDER')return '平台訂單請在 Pad 處理飲品。';
  if(code==='MEAL_DRINK_ORDER_CANCELLED')return '訂單已取消，不能補選飲品。';
  if(code==='STAFF_LOGIN_REQUIRED')return '請先在店內 Pad 登入員工，再繼續操作。';
  if(code==='WEB_COMMAND_CANCEL_FORBIDDEN_OR_REASON_REQUIRED'||code==='ORDER_CORRECTION_PERMISSION_REQUIRED')return '請填寫取消原因，並由管理員在 Admin 確認此帳戶有訂單更正權限。';
  if(code==='DINING_UPDATED')return '餐枱已更新';
  if(code==='DINING_CLEARED')return '餐枱已交回使用';
  if(/UNAUTHORIZED|ACTOR_REVOKED|FORBIDDEN/.test(code))return '員工登入或權限需要確認，請到「更多 → 員工帳戶」重新登入。';
  if(code==='WEB_COMMAND_CORE_OFFLINE')return '店內 Pad 暫未連線，請待連線後再操作。';
  if(/CORE_UPGRADE|RUNTIME_IDENTITY/.test(code))return '店內 Pad 需要啟用最新版本後才能操作。';
  if(code==='DINING_BALANCE_REMAINING')return '仍有未結帳餐點，請先在店內 Pad 完成收款。';
  if(code==='DINING_TABLE_OCCUPIED')return '這張枱已有人使用，請選擇其他餐枱。';
  if(/REQUIRES_UNJOIN/.test(code))return '請先拆開合枱，再轉枱或返回輪候。';
  if(/ORDER_BUSY|STALE|HISTORY_PROTECTED|HOLD_NOT_FOUND/.test(code))return '這張單已有更新或操作未確認，請先更新資料。';
  return /^[A-Z][A-Z0-9_]+$/.test(code)?'操作未完成，請更新資料後再試。':code;
}
function cloudSnapshotUrl(){
  return webSmtAcceptanceMode()
    ?'/api/smm/acceptance/snapshot?storeId=MF01'
    :'/api/smm/snapshot?storeId=MF01';
}

function isRecord(value:unknown):value is Record<string,unknown>{
  return Boolean(value)&&typeof value==='object'&&!Array.isArray(value);
}
function isSnapshot(value:unknown):value is SmmReadModelSnapshot{
  if(!isRecord(value))return false;
  if(typeof value.observedAt!=='string')return false;
  for(const key of ['orders','work','channels','dineSessions','printHealth','refundRequests'] as const){
    if(!Array.isArray(value[key]))return false;
  }
  if(value.menu!==undefined){
    if(!isRecord(value.menu)||typeof value.menu.revision!=='string'||!Array.isArray(value.menu.categories)||!Array.isArray(value.menu.products))return false;
    if(value.menu.combos!==undefined&&!Array.isArray(value.menu.combos))return false;
    if(value.menu.comboPools!==undefined&&!Array.isArray(value.menu.comboPools))return false;
  }
  return true;
}
async function withTimeout<T>(work:Promise<T>,timeoutMs=3500):Promise<T>{
  let timer:number|undefined;
  try{
    return await Promise.race([
      work,
      new Promise<never>((_,reject)=>{timer=window.setTimeout(()=>reject(new Error('SMM_READ_TIMEOUT')),timeoutMs);}),
    ]);
  }finally{if(timer!==undefined)window.clearTimeout(timer);}
}
async function lanRequest(payload:object){
  const config=readSmmLanPwaConfig();if(!config)throw new Error('SMM_LAN_NOT_CONFIGURED');
  const response=await fetch('http://'+config.host+':'+config.port+'/smm/v1/request',{
    method:'POST',headers:{'Content-Type':'application/json'},cache:'no-store',
    body:JSON.stringify({deviceId:config.deviceId,action:'request',payload}),
  });
  if(!response.ok)throw new Error('SMM_LAN_HTTP_'+response.status);
  const json=await response.json() as Record<string,unknown>;
  if(json.ok===false)throw new Error(String(json.code||'SMM_LAN_REJECTED'));
  return json;
}
async function readCloudSnapshot(configOnly=false):Promise<SmmReadModelSnapshot>{
  let config:Partial<SmmReadModelSnapshot>;
  try{config=await reconcileSmmConfig();}
  catch(error){recordSyncDiagnostic('startup',null,error instanceof Error?error.message:'VALIDATION_FAILED');throw error;}
  const merge=(dynamic:SmmReadModelSnapshot)=>{
    const workspace=readSmmLocalWorkspace(),incoming=dynamic.menuPresentation?.revision===config.menu?.revision?dynamic.menuPresentation:undefined;
    const menuPresentation=incoming??(workspace.menuPresentation?.revision===config.menu?.revision?workspace.menuPresentation:undefined);
    if(incoming&&JSON.stringify(incoming)!==JSON.stringify(workspace.menuPresentation)){try{writeSmmLocalWorkspace({...workspace,menuPresentation:incoming});}catch{/* Display metadata remains usable when this browser cannot cache it. */}}
    return Object.freeze({...config,...dynamic,menu:config.menu,menuPresentation,diningTables:dynamic.diningTables??config.diningTables,connectionPath:'INTERNET' as const});
  };
  // Startup validates only the menu. An operational timeout must not consume its 3500ms deadline.
  if(configOnly)return merge(unavailableOperationalSnapshot('NOT_READ'));
  if(config.menuFromCache&&navigator.onLine===false)return merge(unavailableOperationalSnapshot('NETWORK'));
  const session=readSmmStaffSession();
  if(!session)return merge(unavailableOperationalSnapshot('UNAUTHORIZED'));
  const headers:Record<string,string>={Accept:'application/json','x-mfk-smm-session':session.sessionToken};
  const separator=cloudSnapshotUrl().includes('?')?'&':'?';
  const attempt:{status?:number}={};
  try{
    const dynamic=await withTimeout((async()=>{
      const response=await diagnosticFetch('dynamic',cloudSnapshotUrl()+separator+'config=0'+(webSmtAcceptanceMode()?'':'&orders=pages'),{method:'GET',cache:'no-store',headers});
      attempt.status=response.status;
      if(!response.ok)throw new Error('SMM_INTERNET_HTTP_'+response.status);
      const value:unknown=await response.json();
      if(!isSnapshot(value))throw new Error('SMM_INTERNET_SNAPSHOT_INVALID');
      return value;
    })());
    return merge(dynamic);
  }catch(error){
    const status=attempt.status;
    const kind:SmmOperationalFailure['kind']=error instanceof Error&&error.message==='SMM_READ_TIMEOUT'?'TIMEOUT':status===401?'UNAUTHORIZED':status!==undefined?(status>=200&&status<300?'INVALID':'HTTP'):'NETWORK';
    recordSyncDiagnostic('dynamic',status??null,kind==='TIMEOUT'?'MOBILE_OPERATIONS_TIMEOUT':kind==='INVALID'?'MOBILE_OPERATIONS_INVALID':kind==='NETWORK'?'NETWORK_ERROR':kind==='UNAUTHORIZED'?'SMM_STAFF_UNAUTHORIZED':'MOBILE_OPERATIONS_UNAVAILABLE');
    return merge(unavailableOperationalSnapshot(kind,status));
  }
}

function hybridTransport(lan:SmmLanTransport|null,cloud:SmmLanTransport):SmmLanTransport{
  return Object.freeze({
    async send(request:SmmLanOrderRequest,signal:AbortSignal){
      if(lan){
        const local=await lan.send(request,signal);
        if(local.kind!=='UNAVAILABLE')return local;
      }
      return cloud.send(request,signal);
    },
    async readSubmission(
      submissionId:string,
      signal:AbortSignal,
    ):Promise<SmmLanSubmissionReadbackResponse|Readonly<{state:'UNAVAILABLE'}>>{
      if(lan?.readSubmission){
        const local=await lan.readSubmission(submissionId,signal);
        if(!('state'in local&&local.state==='UNAVAILABLE'))return local;
      }
      return cloud.readSubmission
        ?cloud.readSubmission(submissionId,signal)
        :Object.freeze({state:'UNAVAILABLE' as const});
    },
  });
}

export function createPwaRuntimePort():SmmRuntimePort{
  const config=readSmmLanPwaConfig();
  const lan=config?createPwaLanTransport(config):null;
  const cloud=createPwaCloudTransport();
  const orders=createSmmLanOrderAdapter(hybridTransport(lan,cloud),15000);
  const readLanSnapshot=async()=>{
    const raw=await withTimeout(lanRequest({protocolVersion:1,type:'smm.lan.snapshot.v1',storeId:'MF01'}));
    if(!isSnapshot(raw))throw new Error('SMM_LAN_SNAPSHOT_INVALID');
    rememberLanStaff(raw);
    return Object.freeze({...raw,connectionPath:'LAN' as const});
  };
  return Object.freeze({
    portId:'MFK_SMM_PORT_V1' as const,
    subscribeConfigChanges:subscribeSmmConfigChanges,
    async readStartupSnapshot(){
      if(config){try{return await readLanSnapshot();}catch{/* LAN failures fall through to the Internet projection. */}}
      return readCloudSnapshot(true);
    },
    async readSnapshot(){
      if(config){try{return await readLanSnapshot();}catch{/* LAN failures fall through to the Internet projection. */}}
      return readCloudSnapshot();
    },
    async readPaymentEvidence(orderId:string){
      if(navigator.onLine===false)throw Error('核對付款需要連線。');
      const session=readSmmStaffSession();if(!session)throw Error('請先登入員工帳戶。');
      const data=await withTimeout((async()=>{
        const response=await fetch('/api/smm/payment-evidence?'+new URLSearchParams({storeId:'MF01',orderId}),{cache:'no-store',headers:{accept:'image/*','x-mfk-smm-session':session.sessionToken}}).catch(()=>{throw Error('暫時未能讀取付款憑證，請稍後再試。');});
        if(!response.ok)throw Error(response.status===401?'請重新登入員工帳戶。':response.status===403?'未有核對付款權限。':response.status===404||response.status===409?'這張單未有可核對的付款憑證，請在 Pad 核對。':'暫時未能讀取付款憑證，請稍後再試。');
        const evidenceVersion=response.headers.get('x-mfk-payment-evidence-version')||'',total=response.headers.get('x-mfk-payment-total'),expectedTotalMinor=Number(total),expectedTenderId=decodeURIComponent(response.headers.get('x-mfk-payment-tender')||''),tenderLabel=decodeURIComponent(response.headers.get('x-mfk-payment-tender-label')||'');
        if(!/^[a-f0-9]{64}$/.test(evidenceVersion)||total===null||!Number.isSafeInteger(expectedTotalMinor)||expectedTotalMinor<0||!expectedTenderId||!tenderLabel||!response.headers.get('content-type')?.startsWith('image/'))throw Error('付款資料未完整，請在 Pad 核對。');
        return {blob:await response.blob(),evidenceVersion,expectedTotalMinor,expectedTenderId,tenderLabel};
      })());
      const {blob,...facts}=data;return {...facts,objectUrl:URL.createObjectURL(blob)};
    },
    async readPendingDrinks(orderId:string){
      if(navigator.onLine===false)throw Error('補選飲品需要連線。');
      const session=readSmmStaffSession();if(!session)throw Error('請先登入員工帳戶。');
      return withTimeout((async()=>{
        const response=await fetch('/api/smm/pending-drinks?'+new URLSearchParams({storeId:'MF01',orderId}),{cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken}}).catch(()=>{throw Error('暫時未能連接飲品資料。');});
        if(!response.ok)throw Error(response.status===401?'請重新登入員工帳戶。':response.status===404?'這張單未有門店資料，請在 Pad 核對。':'暫時未能讀取飲品，請稍後再試。');
        const data=await response.json().catch(()=>null) as MobilePendingDrinks|null;
        if(!data||data.orderId!==orderId||typeof data.configFingerprint!=='string'||typeof data.expectedUpdatedAt!=='string'||!Array.isArray(data.groups)||!data.groups.every(group=>group?.main?.meal&&Array.isArray(group.choices)&&Array.isArray(group.products)))throw Error('飲品資料未能確認，請重新讀取。');
        return data;
      })());
    },
    async readChannels(){
      if(navigator.onLine===false)throw Error('平台狀態需要連線。');
      const session=readSmmStaffSession();if(!session)throw Error('請先登入員工帳戶。');
      return withTimeout((async()=>{
        const response=await fetch('/api/smm/channels?storeId=MF01',{cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken}}).catch(()=>{throw Error('暫時未能連接平台狀態，請稍後重試。');});
        if(!response.ok)throw Error(response.status===401?'請重新登入員工帳戶。':'暫時未能讀取平台狀態，請稍後再試。');
        return validateSmmChannelStatus(await response.json().catch(()=>null));
      })());
    },
    async readPrintHealth(){
      if(navigator.onLine===false)throw Error('打印狀態需要連線，已讀回資料保留。');
      const session=readSmmStaffSession();if(!session)throw Error('請先登入員工帳戶。');
      return withTimeout((async()=>{
        const response=await fetch('/api/smm/printing?storeId=MF01',{cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken}}).catch(()=>{throw Error('暫時未能連接打印狀態，請稍後重試。');});
        if(!response.ok)throw Error(response.status===401?'請重新登入員工帳戶。':'暫時未能讀取打印狀態，請稍後再試。');
        const value=await response.json();if(!value||value.snapshot===undefined)throw Error('打印狀態資料未完整，請重新讀取。');
        return value.snapshot===null?null:validateMobilePrintHealthSnapshot(value.snapshot);
      })());
    },
    async readBusinessDay(){
      if(navigator.onLine===false)throw Error('營業日記錄需要連線，已讀回資料保留。');
      const session=readSmmStaffSession();if(!session)throw Error('請先登入員工帳戶。');
      return withTimeout((async()=>{
        const response=await fetch('/api/smm/business-day?storeId=MF01',{cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken}}).catch(()=>{throw Error('暫時未能連接營業日記錄，請稍後重試。');});
        if(!response.ok)throw Error(response.status===401?'請重新登入員工帳戶。':'暫時未能讀取營業日記錄，請稍後再試。');
        return validateMobileBusinessDayReceipts(await response.json());
      })());
    },
    async readCapacity(){
      if(navigator.onLine===false)throw Error('產能需要連線，已讀回資料保留。');
      const session=readSmmStaffSession();if(!session)throw Error('請先登入員工帳戶。');
      return withTimeout((async()=>{
        const response=await fetch('/api/smm/capacity?storeId=MF01',{cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken}}).catch(()=>{throw Error('暫時未能連接產能資料，請稍後重試。');});
        if(!response.ok)throw Error(response.status===401?'請重新登入員工帳戶。':'暫時未能讀取產能，請稍後再試。');
        const value=await response.json();if(!/^\d{4}-\d{2}-\d{2}$/.test(value.businessDate)||value.snapshot===undefined)throw Error('產能資料未完整，請重新讀取。');
        return {businessDate:value.businessDate,snapshot:value.snapshot===null?null:validateCapacityPoolReadback(value.snapshot)};
      })());
    },
    async readReports(range:{from?:string;to?:string}={}){
      if(navigator.onLine===false)throw Error('報表需要連線；餐單及購物車仍會保留。');
      const session=readSmmStaffSession();if(!session)throw Error('請先登入員工帳戶。');
      const params=new URLSearchParams({storeId:'MF01'});for(const key of ['from','to'] as const)if(range[key])params.set(key,range[key]!);
      return withTimeout((async()=>{
        const response=await fetch('/api/smm/reports?'+params,{cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken}}).catch(()=>{throw Error('暫時未能連接報表，請稍後重試。');});
        if(!response.ok)throw Error(response.status===401?'請重新登入員工帳戶。':response.status===403?'未有查看報表權限，請在 Admin 員工權限開啟。':response.status===400?'請選擇有效的報表日期。':'暫時未能讀取報表，請稍後再試。');
        return validateSmmDailyReports(await response.json().catch(()=>null));
      })());
    },
    async readOrderPage(input:MobileOrderQuery){
      const query=normalizeMobileOrderQuery(input),session=readSmmStaffSession();
      if(!session)throw new MobileOrderPageError('SMM_STAFF_UNAUTHORIZED',401);
      const params=new URLSearchParams({storeId:'MF01',view:query.view,limit:String(query.limit)});
      for(const key of ['from','to','cursor','search','searchScope'] as const)if(input[key]!==undefined)params.set(key,input[key]!);
      return withTimeout((async()=>{
        const response=await fetch('/api/smm/order-page?'+params,{cache:'no-store',headers:{accept:'application/json','x-mfk-smm-session':session.sessionToken}});
        if(!response.ok)throw new MobileOrderPageError(response.status===409?'MOBILE_ORDER_CURSOR_STALE':response.status===401?'SMM_STAFF_UNAUTHORIZED':'MOBILE_ORDER_PAGE_READ_FAILED',response.status);
        return validateMobileOrderPage(await response.json());
      })());
    },
    submitOrder(intent:SmmPendingIntent):Promise<SmmCommandResult>{
      return orders.submitOrder(intent);
    },
    readSubmission(submissionId:string):Promise<SmmCommandResult>{
      return orders.readSubmission(submissionId);
    },
    async fulfillOrder(input:SmmStoreCommandInput):Promise<SmmCommandResult>{
      const prior=readSmmLocalWorkspace().commandReadbacks.find(item=>item.input.orderId===input.orderId);
      input=prior?.input??input;
      input=Object.freeze({...input,...(input.action==='RESOLVE_DRINK'?{drink:mobileDrinkResolution(input.drink)}:{})});
      const reference={...prior,commandId:prior?.commandId??crypto.randomUUID(),requestId:prior?.requestId??crypto.randomUUID(),input,...(!prior&&webSmtAcceptanceMode()?{target:'web-smt' as const}:{})};
      const request=Object.freeze({...input,protocolVersion:1,type:'smm.lan.fulfillment.v1',requestId:reference.requestId,commandId:reference.commandId,storeId:'MF01'});
      const result=(state:SmmCommandResult['state'],message:string):SmmCommandResult=>Object.freeze({state,message,orderId:input.orderId,commandId:reference.commandId,command:input});
      const remember=(keep:boolean,retryReady=false)=>{const current=readSmmLocalWorkspace();writeSmmLocalWorkspace({...current,commandReadbacks:[...current.commandReadbacks.filter(item=>item.input.orderId!==input.orderId),...(keep?[{...reference,retryReady}]:[])]});};
      try{remember(true);}catch{return result('REJECTED','此手機未能保留操作參考，請重新整理後再試。');}
      if(!prior&&config&&(input.action==='ACCEPT'||input.action==='READY')){
        try{
          const local=await withTimeout(lanRequest(request));
          if(String(local.type)==='smm.lan.fulfillment.result.v1'){
            const result=local as unknown as SmmLanFulfillmentResponse;
            remember(false);
            return Object.freeze({state:result.disposition==='REJECTED'?'REJECTED':'CONFIRMED',message:result.reasonCode??result.canonicalState??'已更新',orderId:result.orderId,commandId:reference.commandId,command:input});
          }
        }catch{/* LAN unavailable: same capability falls through to Internet */}
      }
      const session=readSmmStaffSession();
      if(!session){if(!prior)remember(false);return result(prior?'UNKNOWN':'REJECTED','請先登入員工帳戶，再確認原操作結果。');}
      const target=reference.target==='web-smt'?'&target=web-smt':'';
      try{
      if(!prior||prior.retryReady){
        const response=await fetch('/api/smm/fulfillment?storeId=MF01'+target,{method:'POST',headers:{'content-type':'application/json','x-mfk-smm-session':session.sessionToken},body:JSON.stringify(request)});
        if(!response.ok){const body=await response.json().catch(()=>({})) as any;if(prior&&[401,403].includes(response.status))return result('UNKNOWN',storeCommandMessage('WEB_COMMAND_UNAUTHORIZED'));if(response.status<500&&![408,429].includes(response.status)){remember(false);return result('REJECTED',storeCommandMessage(body.code||'狀態更新未送出'));}return result('UNKNOWN','暫未收到門店確認，請確認原操作結果。');}
      }
      for(const delay of [250,500,1000,1250]){
        await new Promise(resolve=>setTimeout(resolve,delay));
        const readback=await fetch('/api/smm/fulfillment/readback?storeId=MF01&commandId='+encodeURIComponent(request.commandId)+target,{cache:'no-store',headers:{'x-mfk-smm-session':session.sessionToken}});
        if(!readback.ok){
          const body=await readback.json().catch(()=>({})) as any;
          if(readback.status===404&&body.code==='WEB_COMMAND_NOT_FOUND'){remember(true,true);return result('UNKNOWN','門店未收到原操作。可按「以原編號再試」，使用原資料再次送出。');}
          if(readback.status===401||readback.status===403)return result('UNKNOWN',storeCommandMessage('WEB_COMMAND_UNAUTHORIZED'));
          continue;
        }
        const body=await readback.json() as any;
        if(body.state==='CONFIRMED'||body.state==='APPLIED'){
          if(input.action==='CANCEL'){
            if(body.commandId!==request.commandId||body.orderId!==input.orderId||body.action!==input.action||body.receipt?.canonicalState!=='已取消'||body.receipt?.providerState!=='SYNCED'||!['CANCELLED','REJECTED'].includes(body.receipt?.platform?.status))return result('UNKNOWN','平台取消結果未確認，請確認原操作結果。');
            remember(false);return result('CONFIRMED','平台已確認取消。');
          }
          if(input.action==='REVIEW_PAYMENT'){
            if(body.commandId!==request.commandId||body.orderId!==input.orderId||body.action!==input.action||body.decision!==input.decision||body.receipt?.paymentVerificationState!==input.decision)return result('UNKNOWN','付款核對結果未完整，請確認原操作結果。');
            remember(false);return result('CONFIRMED',input.decision==='VERIFIED'?'付款已核對。':'付款標記為未核實；訂單未取消。');
          }
          remember(false);return result('CONFIRMED',storeCommandMessage(String(body.receipt?.canonicalState||body.result?.canonicalState||'已更新')));
        }
        if(body.state==='REJECTED'){remember(false);return result('REJECTED',storeCommandMessage(String(body.receipt?.code||body.result?.reasonCode||'狀態更新被拒絕')));}
        if(body.state==='UNKNOWN')break;
      }
      }catch{/* A lost reply can only be followed by a read of this original command. */}
      return result('UNKNOWN','已送出，暫未收到門店確認；請確認原操作結果。');
    },
  });
}
